import { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react'
import { ExternalLink } from 'lucide-react'
import { cn } from 'utils/cn'

export interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'prefix'> {
    icon?: ReactNode
    showIcon?: boolean
    disabled?: boolean
}

/** `className` is merged over the base styles, so a caller can set its own text size or weight. */
export const Link = ({ children, icon, showIcon = true, disabled = false, className, style, ...props }: LinkProps) => {
    const baseClass =
        'group inline-flex items-start justify-center gap-0.5 whitespace-nowrap px-0.5 text-sm font-medium leading-[18px] text-foreground underline decoration-border underline-offset-[3px] outline-none transition-[opacity,text-decoration-color,box-shadow] duration-150'
    const stateClass = disabled
        ? 'cursor-not-allowed opacity-50'
        : 'cursor-pointer hover:opacity-80 hover:decoration-foreground-muted active:opacity-80 active:decoration-foreground-muted focus-visible:rounded-xl focus-visible:bg-surface focus-visible:decoration-foreground-muted focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface'
    const iconClass = disabled
        ? 'flex shrink-0 items-center pt-0.5 opacity-60'
        : 'flex shrink-0 items-center pt-0.5 opacity-60 transition-opacity duration-150 group-hover:opacity-100 group-active:opacity-100 group-focus-visible:opacity-100'

    return (
        <a
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : undefined}
            className={cn(baseClass, stateClass, className)}
            style={style}
            {...(disabled ? { onClick: (e: MouseEvent<HTMLAnchorElement>) => e.preventDefault() } : {})}
            {...props}
        >
            <span>{children}</span>
            {showIcon && <span className={iconClass}>{icon || <ExternalLink size={16} strokeWidth={1.5} />}</span>}
        </a>
    )
}
